import { Fragment, type ReactNode } from "react";

type AssistantRichTextProps = {
  text: string;
};

const inlinePattern = /(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\)|\*[^*\n]+\*|_[^_\n]+_)/g;

export function AssistantRichText({ text }: AssistantRichTextProps) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: ReactNode[] = [];

  for (let index = 0; index < lines.length;) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = line.match(/^```([\w-]+)?\s*$/);
    if (fence) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }
      index += index < lines.length ? 1 : 0;
      blocks.push(<pre key={`code-${index}`}><code data-language={fence[1]}>{code.join("\n")}</code></pre>);
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const content = renderInline(heading[2], `heading-${index}`);
      if (heading[1].length === 1) blocks.push(<h3 key={`heading-${index}`}>{content}</h3>);
      if (heading[1].length === 2) blocks.push(<h4 key={`heading-${index}`}>{content}</h4>);
      if (heading[1].length === 3) blocks.push(<h5 key={`heading-${index}`}>{content}</h5>);
      index += 1;
      continue;
    }

    if (/^([-*_])(?:\s*\1){2,}\s*$/.test(line)) {
      blocks.push(<hr key={`rule-${index}`} />);
      index += 1;
      continue;
    }

    const headerCells = tableCells(line);
    const dividerCells = index + 1 < lines.length ? tableCells(lines[index + 1]) : undefined;
    if (headerCells && dividerCells && headerCells.length === dividerCells.length && dividerCells.every((cell) => /^:?-{3,}:?$/.test(cell))) {
      const tableStart = index;
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length) {
        const row = tableCells(lines[index]);
        if (!row || row.length !== headerCells.length) break;
        rows.push(row);
        index += 1;
      }
      blocks.push(
        <div className="assistant-table-wrap" role="region" aria-label="Assistant response table" tabIndex={0} key={`table-${tableStart}`}>
          <table>
            <thead><tr>{headerCells.map((cell, cellIndex) => <th scope="col" key={`header-${cellIndex}`}>{renderInline(cell, `table-${tableStart}-header-${cellIndex}`)}</th>)}</tr></thead>
            <tbody>{rows.map((row, rowIndex) => <tr key={`row-${rowIndex}`}>{row.map((cell, cellIndex) => <td key={`cell-${cellIndex}`}>{renderInline(cell, `table-${tableStart}-${rowIndex}-${cellIndex}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push(<blockquote key={`quote-${index}`}>{renderInline(quote.join(" "), `quote-${index}`)}</blockquote>);
      continue;
    }

    const listItem = line.match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
    if (listItem) {
      const ordered = /\d+\./.test(listItem[2]);
      const items: ReactNode[] = [];
      while (index < lines.length) {
        const item = lines[index].match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
        if (!item || /\d+\./.test(item[2]) !== ordered) break;
        items.push(<li key={`item-${index}`}>{renderInline(item[3], `item-${index}`)}</li>);
        index += 1;
      }
      blocks.push(ordered
        ? <ol key={`list-${index}`}>{items}</ol>
        : <ul key={`list-${index}`}>{items}</ul>);
      continue;
    }

    const paragraph: string[] = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !startsBlock(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push(<p key={`paragraph-${index}`}>{renderInline(paragraph.join(" "), `paragraph-${index}`)}</p>);
  }

  return <div className="assistant-rich-text">{blocks}</div>;
}

function startsBlock(line: string) {
  return /^```|^#{1,3}\s+|^>\s?|^(\s*)([-*+]|\d+\.)\s+/.test(line)
    || /^([-*_])(?:\s*\1){2,}\s*$/.test(line)
    || Boolean(tableCells(line));
}

function tableCells(line: string) {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return undefined;
  const source = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  const cells: string[] = [];
  let cell = "";
  let escaped = false;
  for (const character of source) {
    if (escaped) {
      cell += character;
      escaped = false;
    } else if (character === "\\") {
      escaped = true;
    } else if (character === "|") {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += character;
    }
  }
  if (escaped) cell += "\\";
  cells.push(cell.trim());
  return cells.length >= 2 ? cells : undefined;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const content: ReactNode[] = [];
  let cursor = 0;
  let tokenIndex = 0;

  for (const match of text.matchAll(inlinePattern)) {
    const start = match.index ?? 0;
    if (start > cursor) content.push(text.slice(cursor, start));

    const token = match[0];
    const key = `${keyPrefix}-${tokenIndex}`;
    if (token.startsWith("**") || token.startsWith("__")) {
      content.push(<strong key={key}>{renderInline(token.slice(2, -2), key)}</strong>);
    } else if (token.startsWith("`")) {
      content.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("[")) {
      const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      content.push(link
        ? <a key={key} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>
        : token);
    } else {
      content.push(<em key={key}>{renderInline(token.slice(1, -1), key)}</em>);
    }

    cursor = start + token.length;
    tokenIndex += 1;
  }

  if (cursor < text.length) content.push(text.slice(cursor));
  return content.map((node, index) => <Fragment key={`${keyPrefix}-part-${index}`}>{node}</Fragment>);
}
